import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Trophy, ChevronRight, MapPin, Zap, Lock, ArrowLeft, Crown } from 'lucide-react';
import { useBracketStore } from '@/stores/bracketStore';
import { useAuthStore } from '@/stores/authStore';
import { getGamesByRound } from '@/data/tournament';
import { getTeamsByRegion as getStaticTeamsByRegion } from '@/data/teams';
import BracketRegion from '@/components/bracket/BracketRegion';
import Matchup from '@/components/bracket/Matchup';
import ProgressRing from '@/components/gaming/ProgressRing';
import toast from 'react-hot-toast';
import { useTeamsStore } from '@/stores/teamsStore';

type View = 'overview' | 'region' | 'final-four';
type Region = 'east' | 'west' | 'south' | 'midwest';

const REGIONS: { key: Region; label: string; color: string; accent: string; glowClass: string; borderHover: string }[] = [
  { key: 'east', label: 'East', color: 'from-blue-500/20 to-blue-600/5', accent: 'text-blue-400', glowClass: 'region-glow-blue', borderHover: 'hover:border-blue-500/30' },
  { key: 'west', label: 'West', color: 'from-emerald-500/20 to-emerald-600/5', accent: 'text-emerald-400', glowClass: 'region-glow-emerald', borderHover: 'hover:border-emerald-500/30' },
  { key: 'south', label: 'South', color: 'from-rose-500/20 to-rose-600/5', accent: 'text-rose-400', glowClass: 'region-glow-rose', borderHover: 'hover:border-rose-500/30' },
  { key: 'midwest', label: 'Midwest', color: 'from-amber-500/20 to-amber-600/5', accent: 'text-amber-400', glowClass: 'region-glow-amber', borderHover: 'hover:border-amber-500/30' },
];

const BracketPage: React.FC = () => {
  const { bracketId } = useParams<{ bracketId: string }>();
  const [view, setView] = useState<View>('overview');
  const [activeRegion, setActiveRegion] = useState<Region>('east');
  const { bracket, isBootstrapping, isLoadingBracket, error, loadUserBracket, loadBracket, createBracket, getPicksProgress, submitBracket, isSubmitting, isUpdatingPick, getRegionProgress } = useBracketStore();
  const session = useAuthStore(s => s.session);
  const isReady = useAuthStore(s => s.isReady);
  const storeGetTeamsByRegion = useTeamsStore(s => s.getTeamsByRegion);
  const progress = getPicksProgress();
  const [tiebreakerPoints, setTiebreakerPoints] = useState<string>('');
  const [isCreatingBracket, setIsCreatingBracket] = useState(false);

  useEffect(() => {
    // Eagerly load teams so bracket matchups can display team names/logos
    useTeamsStore.getState().fetchTeams();
  }, []);

  // Use a ref to track if we've already loaded this bracket to prevent re-fetch loops
  const loadedBracketRef = React.useRef<string | null>(null);
  
  useEffect(() => {
    if (!isReady || !session?.access_token) return;
    
    if (bracketId) {
      // Only load if we haven't already loaded this exact bracket
      if (loadedBracketRef.current !== bracketId) {
        loadedBracketRef.current = bracketId;
        loadBracket(bracketId);
      }
    } else {
      // Only bootstrap if we haven't loaded any bracket yet
      const currentBracket = useBracketStore.getState().bracket;
      if (!currentBracket && loadedBracketRef.current !== 'user') {
        loadedBracketRef.current = 'user';
        loadUserBracket();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, bracketId]);

  if (isBootstrapping || isLoadingBracket) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
          <p className="text-sm text-white/40">Loading bracket...</p>
        </div>
      </div>
    );
  }

  // No bracket loaded — user has no brackets yet, offer to create one
  if (!bracket && !error && !bracketId) {
    const handleCreate = async () => {
      setIsCreatingBracket(true);
      try {
        await createBracket('My March Madness Bracket', 'march-madness-2026');
      } catch {
        // error is set in the store
      } finally {
        setIsCreatingBracket(false);
      }
    };

    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-lg w-full text-center">
          <div className="h-16 w-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-500/20 to-primary-600/10 border border-primary-500/20 flex items-center justify-center">
            <Trophy className="h-8 w-8 text-primary-400" />
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2">No Bracket Yet</h1>
          <p className="text-white/40 text-sm mb-6 max-w-sm mx-auto">
            You don't have a bracket for this tournament. Create one to get started.
          </p>
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={handleCreate}
              disabled={isCreatingBracket}
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-6 py-2.5 rounded-lg transition-colors duration-150"
            >
              {isCreatingBracket ? (
                <><span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Creating...</>
              ) : (
                <><Trophy className="h-4 w-4" /> Create My Bracket</>
              )}
            </button>
            <Link to="/" className="text-xs text-white/40 hover:text-white/60">
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error && !bracket) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <p className="text-white/40 text-sm mb-4">{error}</p>
          <div className="flex items-center justify-center gap-3">
            <button onClick={() => window.location.reload()} className="text-sm text-primary-400 hover:text-primary-300">
              Try again
            </button>
            <span className="text-white/20">·</span>
            <button onClick={() => { window.location.href = '/bracket'; }} className="text-sm text-white/50 hover:text-white/70">
              Go to My Bracket
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isLocked = Boolean(bracket?.isLocked);

  // Find first incomplete region for "Next up" badge
  const firstIncompleteRegion = REGIONS.find(r => getRegionProgress(r.key).percentage < 100)?.key;

  // Region detail view
  if (view === 'region') {
    const region = REGIONS.find(r => r.key === activeRegion)!;
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="sticky top-0 z-30 bg-[#0a0a1a]/80 backdrop-blur-xl border-b border-white/[0.06]">
          <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-4">
            <button 
              onClick={() => setView('overview')}
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white bg-white/[0.06] hover:bg-white/[0.1] px-3 py-1.5 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Overview
            </button>
            <div className="h-4 w-px bg-white/10" />
            <h2 className={`text-sm font-semibold ${region.accent}`}>{region.label} Region</h2>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 py-6">
          {isLocked && (
            <div className="mb-4 rounded-lg border border-primary-500/20 bg-primary-500/10 px-4 py-2 text-sm text-white/80">
              <span className="font-semibold text-primary-300">Locked:</span> Your bracket has been submitted. Picks can't be changed.
            </div>
          )}
          <div className={isLocked ? 'opacity-90' : ''}>
            <BracketRegion region={activeRegion} />
          </div>
        </div>
      </div>
    );
  }

  // Final Four view
  if (view === 'final-four') {
    const ff = getGamesByRound('final_four');
    const champ = getGamesByRound('championship')[0];
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="sticky top-0 z-30 bg-[#0a0a1a]/80 backdrop-blur-xl border-b border-white/[0.06]">
          <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-4">
            <button onClick={() => setView('overview')} className="flex items-center gap-2 text-sm text-white/70 hover:text-white bg-white/[0.06] hover:bg-white/[0.1] px-3 py-1.5 rounded-lg transition-colors">
              <ArrowLeft className="h-4 w-4" /> Overview
            </button>
            <div className="h-4 w-px bg-white/10" />
            <h2 className="text-sm font-semibold text-amber-400">Final Four & Championship</h2>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="grid md:grid-cols-3 gap-6 items-start">
            <div className="space-y-3">
              <p className="text-xs font-medium text-white/50 uppercase tracking-wider">Semifinal 1</p>
              <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
                {ff[0] ? <Matchup game={ff[0]} size="lg" showConfidence /> : <p className="text-white/20 text-sm">TBD</p>}
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-xs font-medium text-amber-400/80 uppercase tracking-wider text-center">Championship</p>
              <div className="rounded-xl bg-gradient-to-b from-amber-500/[0.08] to-transparent border border-amber-500/25 p-4 gold-glow-border">
                <div className="flex justify-center mb-3">
                  <div className="h-8 w-8 rounded-full bg-amber-500/15 flex items-center justify-center">
                    <Trophy className="h-4 w-4 text-amber-400" />
                  </div>
                </div>
                {champ ? <Matchup game={champ} size="lg" showConfidence /> : <p className="text-white/20 text-sm text-center">TBD</p>}
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-xs font-medium text-white/50 uppercase tracking-wider">Semifinal 2</p>
              <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
                {ff[1] ? <Matchup game={ff[1]} size="lg" showConfidence /> : <p className="text-white/20 text-sm">TBD</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Overview
  return (
    <div className="flex-1 overflow-y-auto">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-[#0a0a1a]/80 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-[15px] font-semibold text-white">March Madness 2026</h1>
          </div>
          <div className="flex items-center gap-3">
            {progress.percentage === 100 && !bracket?.isLocked && (
              <span className="text-xs text-emerald-400 font-medium">✅ All picks made</span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Hero header card */}
        <div className="mb-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary-500/20 to-primary-600/10 border border-primary-500/20 flex items-center justify-center flex-shrink-0">
                <Trophy className="h-7 w-7 text-primary-400" />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white">
                  {bracket?.name || 'My Bracket'}
                </h2>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                    isLocked 
                      ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' 
                      : 'bg-blue-500/15 text-blue-400 border border-blue-500/20'
                  }`}>
                    {isLocked ? <Lock className="h-3 w-3" /> : null}
                    {isLocked ? 'Locked' : 'Draft'}
                  </span>
                  <span className="text-xs text-white/40">March Madness 2026</span>
                </div>
              </div>
            </div>

            {/* Progress ring + pick count */}
            <div className="flex items-center gap-5">
              <div className="text-right hidden sm:block">
                <p className="text-2xl font-bold text-white tabular-nums">{progress.completed}<span className="text-white/30">/{progress.total}</span></p>
                <p className="text-xs text-white/40 mt-0.5">picks made</p>
              </div>
              <ProgressRing
                progress={progress.percentage}
                size="lg"
                color="orange"
                animated
                showPercentage
              />
            </div>
          </div>

          {/* Progress bar (full width, below hero content) */}
          <div className="mt-6 pt-5 border-t border-white/[0.06]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-white/40">Overall Progress</span>
              <span className="text-xs font-medium text-white/60 tabular-nums">{progress.percentage}%</span>
            </div>
            <div className="w-full h-2 bg-white/[0.06] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary-500 to-primary-400 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
          </div>
        </div>

        {isLocked && (
          <div className="mb-6 rounded-lg border border-primary-500/20 bg-primary-500/10 px-4 py-2 text-sm text-white/80">
            <span className="font-semibold text-primary-300">Submitted / Locked:</span> Picks are final. You can still view and share.
          </div>
        )}

        {/* Region grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {REGIONS.map((region) => {
            const regionPct = getRegionProgress(region.key).percentage;
            const isNextUp = region.key === firstIncompleteRegion && !isLocked;
            
            // Get top 4 seeds for this region
            const storeTeams = storeGetTeamsByRegion(region.key);
            const regionTeams = storeTeams.length > 0 ? storeTeams : getStaticTeamsByRegion(region.key);
            const topSeeds = regionTeams
              .filter(t => t.seed && t.seed <= 4)
              .sort((a, b) => (a.seed || 99) - (b.seed || 99))
              .slice(0, 4);

            return (
              <button
                key={region.key}
                onClick={() => { setActiveRegion(region.key); setView('region'); }}
                className={`group text-left rounded-xl border border-white/[0.06] ${region.borderHover} p-5 transition-all duration-200 ${region.glowClass} relative bg-white/[0.015]`}
              >
                {/* "Next up" badge */}
                {isNextUp && (
                  <div className="absolute -top-2 right-4 z-10">
                    <span className="badge-next-up text-[10px] font-semibold uppercase tracking-wider text-primary-400 bg-primary-500/15 px-2 py-0.5 rounded-full border border-primary-500/25">
                      Next up
                    </span>
                  </div>
                )}

                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className={`text-lg font-semibold ${region.accent}`}>{region.label}</h3>
                    <p className="text-xs text-white/50 mt-0.5">16 teams · 15 games</p>
                  </div>
                  <div className="flex items-center gap-1.5 text-white/20 group-hover:text-white/40 transition-colors">
                    <span className="text-xs font-medium tabular-nums">{regionPct}%</span>
                    <ChevronRight className="h-4 w-4" />
                  </div>
                </div>

                {/* Progress bar */}
                <div className="w-full h-1 bg-white/[0.06] rounded-full overflow-hidden mb-4">
                  <div 
                    className={`h-full rounded-full bg-gradient-to-r ${region.color.replace('/20', '').replace('/5', '')}`}
                    style={{ width: `${regionPct}%` }}
                  />
                </div>

                {/* Top seeds preview with logos */}
                <div className="flex items-center gap-2">
                  {topSeeds.map((team) => (
                    <div key={team.id} className="flex items-center gap-1 bg-white/[0.04] px-1.5 py-0.5 rounded">
                      {team.logo && (
                        <img
                          src={team.logo}
                          alt={team.shortName}
                          className="w-4 h-4 rounded-sm object-contain"
                          loading="lazy"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <span className="text-[11px] text-white/35 font-medium">{team.shortName}</span>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        {/* Final Four card — gold/championship theme */}
        <button
          onClick={() => setView('final-four')}
          className="w-full text-left rounded-xl border border-amber-500/15 hover:border-amber-500/30 bg-amber-500/[0.03] p-5 transition-all duration-200 group gold-glow-border ff-card-hover"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/20 flex items-center justify-center">
                <Crown className="h-6 w-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-white group-hover:text-amber-50 transition-colors">Final Four & Championship</h3>
                <p className="text-xs text-amber-400/50 mt-0.5">3 games remaining · Pick your champion</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-amber-400/30 group-hover:text-amber-400 transition-colors" />
          </div>
        </button>

        {/* Submit section with tiebreaker */}
        {progress.percentage === 100 && !bracket?.isLocked && (
          <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-6">
            <h3 className="text-lg font-semibold text-white mb-1">Ready to Submit!</h3>
            <p className="text-sm text-white/50 mb-5">All 63 picks made. Set your tiebreaker and lock it in.</p>
            
            <div className="mb-5">
              <label className="block text-sm font-medium text-white/70 mb-2">
                🏆 Tiebreaker: Total combined points in the Championship game
              </label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="e.g. 145"
                value={tiebreakerPoints}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  if (v === '' || (Number(v) >= 0 && Number(v) <= 300)) setTiebreakerPoints(v);
                }}
                className="w-full max-w-xs bg-dark-500 border border-dark-300 rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500/40"
              />
              <p className="text-xs text-white/30 mt-1.5">Used to break ties on the leaderboard</p>
            </div>

            <button
              onClick={async () => {
                const tb = tiebreakerPoints ? Number(tiebreakerPoints) : undefined;
                try {
                  await submitBracket(tb);
                  toast.success('🎉 Bracket submitted!');
                } catch {
                  toast.error('Failed to submit');
                }
              }}
              disabled={isSubmitting || isUpdatingPick}
              className="bg-primary-500 hover:bg-primary-600 text-white font-medium px-6 py-2.5 rounded-lg transition-colors disabled:opacity-50 text-sm"
            >
              {isSubmitting ? 'Submitting...' : isUpdatingPick ? 'Saving pick…' : '🔒 Submit & Lock Bracket'}
            </button>
          </div>
        )}

        {/* Quick stats — glass-morphism */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          {[
            { label: 'Points', value: bracket?.score || 0, icon: Zap, color: 'text-amber-400' },
            { label: 'Picks Made', value: `${progress.completed}/${progress.total}`, icon: MapPin, color: 'text-emerald-400' },
            { label: 'Status', value: bracket?.isLocked ? 'Locked' : 'Draft', icon: Lock, color: bracket?.isLocked ? 'text-red-400' : 'text-blue-400' },
          ].map((stat, i) => (
            <div key={i} className="glass-stat rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
                <span className="text-[11px] text-white/50 uppercase tracking-wider font-medium">{stat.label}</span>
              </div>
              <p className="text-2xl font-bold text-white tabular-nums">{stat.value}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export { BracketPage };
